import { CircularProgress } from '@material-ui/core';
import { BlikNoCode, TimePending } from 'assets/icons';
import AlertIcon from 'assets/images/notifications/exclamationError.svg?react';
import dataLayerPaymentManager from 'features/deposit/dataLayerPaymentManager';
import { PaymentMethods } from 'features/deposit/interface';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FINANCIAL_THRESHOLDS } from 'shared/constants/financial-thresholds';
import { CustomerApi } from '../../api';
import { PaymentDepositRequest } from '../../api/customer.api';
import ModalDefaultContent from '../../shared/components/Modal/component/modalDefaultContent';
import { FastPaymentConfig, PaymentProviderId, SectionNavigation, TEMPORARY_ACCOUNT_ERRORCODE } from '../../shared/constants/common';
import { setDataLayer, VerificationEvents } from '../../shared/constants/datalayer';
import { useFastPaymentFeatureFlag } from '../../shared/featureFlags';
import { getGrowthbookUUID } from '../../shared/hoc/GrowthBookWrapper';
import useSectionNavigation from '../../shared/hooks/useSectionNavigation';
import { BalanceState, State } from '../../shared/interface';
import { modalOpen, setModalContent } from '../../shared/store/modal.action';
import { getUserDetails, isLoggedin, updateUserDetails } from '../../shared/util/utility';
import './styles.scss';

interface Props {
	device?: 'desktop' | 'mobile';
	onReady?: () => void;
	location: 'mobile-homepage' | 'mobile-coupon' | 'web-coupon';
}
const FastPayment: React.FC<Props> = (props) => {
	const isFastPayment = useFastPaymentFeatureFlag();
	const [info, setInfo] = useState({
		show: false,
		amount: 0,
	});
	const [loading, setLoading] = useState(false);
	const balanceData = useSelector<State, BalanceState>((state) => state.cpPanel.balanceData);
	const userDetails = useSelector<State, any>((state) => state.login.userDetails);
	const [blikStatus, setBlikStatus] = useState<any>(getUserDetails().additionalResponseText);
	const dispatch = useDispatch();
	const { goToFirstNavigationView } = useSectionNavigation();
	const { t } = useTranslation();
	const isUser = isLoggedin();

	const openPendingModal = () => {
		dispatch(
			setModalContent(
				<ModalDefaultContent
					title={t('modalPendingTitle')}
					description={t('modalPendingDescription')}
					icon={<TimePending />}
					buttons={[
						{
							type: 'button',
							onClick: () => {
								dispatch(modalOpen(false));
							},
							text: t('modalPendingButton'),
						},
					]}
				/>,
			),
		);
		dispatch(modalOpen(true));
	};

	const openGenericErrorModal = () => {
		dispatch(
			setModalContent(
				<ModalDefaultContent
					title={t('modalGenericErrorTitle')}
					description={t('modalGenericErrorDescription')}
					icon={<AlertIcon />}
					buttons={[
						{
							btnType: 'primary100',
							type: 'button',
							onClick: () => {
								dispatch(modalOpen(false));
							},
							text: t('cancel'),
						},
						{
							type: 'button',
							onClick: () => {
								charge();
								dispatch(modalOpen(false));
							},
							text: t('tryAgain'),
						},
					]}
				/>,
			),
		);
		dispatch(modalOpen(true));
	};

	const openTemporaryAccountModal = () => {
		dispatch(
			setModalContent(
				<ModalDefaultContent
					title={t('modalGenericErrorTitle')}
					description={t('errorDeposit43')}
					icon={<AlertIcon />}
					buttons={[
						{
							btnType: 'primary100',
							type: 'button',
							onClick: () => {
								dispatch(modalOpen(false));
							},
							text: t('cancel'),
						},
						{
							type: 'button',
							onClick: () => {
								dispatch(modalOpen(false));
								goToFirstNavigationView(SectionNavigation.ACCOUNT_VERIFICATION);
								setDataLayer(VerificationEvents.DATALAYER_VERIFICATION_FORM_START);
							},
							text: t('verify'),
						},
					]}
				/>,
			),
		);
		dispatch(modalOpen(true));
	};

	const charge = async () => {
		setLoading(true);
		const depositData: PaymentDepositRequest = {
			providerId: PaymentProviderId.BLIK,
			transactionAmount: info.amount.toString(),
			customer: {
				email: userDetails.email,
			},
			additionalData: JSON.stringify({
				saveBlikAlias: 'true',
				regulationAccept: 'true',
				blikAlias: 'true',
			}),
		};
		dataLayerPaymentManager.onPaymentStarts({
			value: info.amount,
			balance_before_payment: balanceData.mainBalance as number,
			payment_method: [PaymentMethods.BLIK_NO_CODE],
			payment_stake_method: ['last'],
			deposit_count: [balanceData.depositCounter ? balanceData.depositCounter : 0],
			fast_payment: dataLayerPaymentManager.getPaymentLocationForAnalytics(props.location),
		});

		await CustomerApi.deposit(depositData)
			.then((resp: any) => {
				setLoading(false);
				if (resp && resp.data && !resp.data.isError) {
					openPendingModal();
				} else {
					if (resp.data.data.responseCode === TEMPORARY_ACCOUNT_ERRORCODE) {
						openTemporaryAccountModal();
					} else {
						openGenericErrorModal();
					}
				}
			})
			.catch((err: any) => {
				openGenericErrorModal();
				setLoading(false);
			});
	};
	const getBlikStatus = async () => {
		if (blikStatus === undefined) {
			const activeBlik = await CustomerApi.initialize({
				providerId: PaymentProviderId.BLIK,
				transactionAmount: FINANCIAL_THRESHOLDS.MIN_DEPOSIT_AMOUNT.toString(),
				transactionCurrency: 'PLN',
			})
				.then((resp: any) => {
					if (resp && resp.data && !resp.data.isError) {
						return resp.data.data.initData.additionalResponseText || '';
					}
				})
				.catch(() => {
					return false;
				});
			updateUserDetails('additionalResponseText', activeBlik);
			setBlikStatus(activeBlik);
			return activeBlik;
		} else {
			return blikStatus;
		}
	};
	const initializeData = async (newAmount = 0) => {
		const amount = newAmount;

		if (!balanceData.mainBalance || balanceData.mainBalance < FastPaymentConfig.MAX_DEPOSIT_AMOUNT) {
			const currentBlikStatus = await getBlikStatus();
			if (currentBlikStatus) {
				setDataLayer({
					event: 'fast-payment-prod_custom',
					'fast-payment-prod_event_category': 'experiment',
					'fast-payment-prod_variation_id': isFastPayment.value ? 1 : 0,
					'fast-payment-prod_gb_user_id': getGrowthbookUUID(),
				});
				if (isFastPayment.value) {
					props.onReady?.();
					if (amount > FastPaymentConfig.MIN_LAST_DEPOSIT) {
						setInfo({ show: true, amount: amount });
					} else {
						setInfo({ show: true, amount: FastPaymentConfig.MIN_LAST_DEPOSIT });
					}
				}
			} else {
				setInfo({ show: false, amount: 0 });
			}
		} else {
			setInfo({ show: false, amount: 0 });
		}
	};

	useEffect(() => {
		if (isUser) {
			let lastDepositAmount = 0;
			try {
				lastDepositAmount = JSON.parse(JSON.parse(userDetails?.depositInfo || '"{}"'))?.lastDepositAmount as number;
			} catch (e) {}
			userDetails?.login && initializeData(lastDepositAmount);
		} else {
			setInfo({ show: false, amount: 0 });
		}
	}, [isUser, isFastPayment.value]);

	useEffect(() => {
		if (isUser && balanceData?.depositInfo) {
			let last = 0;
			try {
				last = JSON.parse(JSON.parse(balanceData?.depositInfo || '"{}"'))?.lastDepositAmount;
			} catch (e) {}
			initializeData(last);
		}
	}, [balanceData]);

	return info.show ? (
		<div className={`fastPayment__content ${props.location}`}>
			{loading && (
				<div className="__loader">
					<CircularProgress color={'inherit'} />
				</div>
			)}
			<div className="__title">{t('depositOnClick', { amount: info.amount })}</div>
			<div onClick={() => charge()} className="__button">
				<div>{t('depositWith')}</div>
				<BlikNoCode id={props.location} className="size-[38px]" />
			</div>
		</div>
	) : null;
};

export default memo(FastPayment);
